/**
 * i18n scaffold：zh-TW 是預設與**事實來源**，en 是翻譯。
 * 刻意用「扁平 key + 型別綁定」而不是 i18next —— 只有兩個語系、幾十個字串，
 * 多一個執行期依賴不划算；而且 `Dict` 的型別會強迫 en 補齊所有 key（漏翻譯 = build 失敗）。
 */

import { config } from '../config';

/**
 * Guest notice copy is derived from the build's actual persistence mode（C03 §5.1）
 *
 * ## 為什麼這一句要從 `config` 算出來，而不是寫死
 *
 * `lobby.guestNotice` 是**對玩家的隱私承諾**，而且是會被截圖的那種句子。
 * 它必須跟**這個 build 實際的行為**一致 —— 不是跟契約一致，也不是跟「我們打算做的事」一致。
 *
 * 落地前那一句（「關掉分頁身分就會消失，對局紀錄不會跟著你」）在 `session` 模式下是真的，
 * 在 `cookie` 模式下是**假的**；反過來也一樣。把兩句都留著、由同一個開關挑，
 * 是為了讓「文案說謊」在構造上不可能發生：不管誰用什麼旗標 build，
 * 玩家讀到的都是這個 build 真的會做的事。
 *
 * ⚠️ 兩句都必須說完四件事，不可以只留好聽的那幾件：
 *   1. 這是匿名身分，不用註冊；
 *   2. 紀錄**留得住到什麼程度**（`cookie`：這個瀏覽器；`session`：這個分頁）；
 *   3. **什麼情況會沒有**（換裝置、換瀏覽器、清瀏覽資料）；
 *   4. 註冊之後紀錄會跟著走（C03 §2 的 `POST /auth/guest/upgrade`，只增不減）。
 */
export const guestNoticeCopy = {
  'zh-TW': {
    cookie:
      '你正在以匿名身分遊玩，不需要註冊。這個身分記在這台裝置的這個瀏覽器裡 —— 關掉分頁再回來還是同一個你，之前的對局紀錄也還在。' +
      '但它只留在這裡：換一台裝置、換一個瀏覽器、或清掉本站的瀏覽資料，這個身分和紀錄就都找不回來了。' +
      '之後註冊正式帳號時，這些紀錄會一起帶進去。',
    session: '你正在以匿名身分遊玩。關掉分頁身分就會消失，對局紀錄不會跟著你。',
  },
  en: {
    cookie:
      'You are playing anonymously — no signup needed. This identity is remembered in this browser on this device: close the tab, come back later, and you are still the same player, with your match history. ' +
      'It lives here and nowhere else — another device, another browser, or clearing this site’s browsing data loses the identity and the history for good. ' +
      'Create an account later and that history comes with you.',
    session: 'You are playing anonymously. Close this tab and the identity is gone — match history will not follow you.',
  },
} as const;

export const zhTW = {
  'app.title': '剪刀石頭布',
  'app.subtitle': 'eLab Gaming Platform',
  'app.tagline': '免註冊、可驗證公平的雙人對戰',

  'nav.lobby': '大廳',
  'nav.howto': '玩法',
  'nav.fairness': '公平性',
  'lang.switch': 'English',

  'guest.creating': '正在建立匿名身分…',
  'guest.you': '你',
  'guest.failed': '無法建立匿名身分',
  'guest.retry': '重試',

  'lobby.title': '選一個玩法',
  'lobby.online': '線上人數',
  'lobby.queued': '排隊中',
  'lobby.openTables': '空桌',
  'lobby.variant.rps-bo1': '一戰定生死',
  'lobby.variant.rps-bo3': '三戰兩勝',
  'lobby.variant.rps-bo5': '五戰三勝',
  'lobby.variant.rps-first-to-5': '先拿 5 分',
  'lobby.mode.title': '對戰方式',
  'lobby.mode.random': '隨機媒合',
  'lobby.mode.random.hint': '丟進佇列，配到誰算誰',
  'lobby.mode.direct': '指名對決',
  'lobby.mode.direct.hint': '指定對手的 subject id（需登入玩家）',
  'lobby.mode.invite': '邀請碼',
  'lobby.mode.invite.hint': '產生連結，傳給朋友',
  'lobby.mode.vs_ai': '挑戰 AI',
  'lobby.mode.vs_ai.hint': '和平台訓練出來的策略對打',
  'lobby.aiProfile': 'AI 對手',
  // ★ C02 §4.1：AI 名單由伺服器給。拿不到的時候要說得出「為什麼不能按」，
  //   而不是給一顆按下去必然 422 的按鈕（修復前就是這樣，玩家看到的是
  //   `AI_PROFILE_NOT_FOUND: unknown ai_profile 'nash'` 這種未翻譯的技術字串）。
  'lobby.aiProfile.unavailable': '這個伺服器目前沒有開放可以挑戰的 AI 對手。',
  'lobby.opponentId': '對手 subject id',
  'lobby.start': '開始配對',
  'lobby.queueing': '配對中…',
  'lobby.cancel': '取消配對',
  'lobby.queuePosition': '已等待 {seconds} 秒',
  // ★ 排隊逾時 —— 修復前 `strings.ts` 裡**一句都沒有**。
  //   `LobbyPage` 的輪詢用 `.catch(() => undefined)` 把票券過期的 404 一起吞掉，
  //   於是畫面永遠停在「配對中… 已等待 N 秒」；就算收到 `expired`，
  //   spinner 也只是靜靜消失，玩家分不出「還在等」與「已經沒在等了」。
  'lobby.queueExpired': '這次配對逾時了 —— 排隊票券已經過期，沒有配到對手。再按一次就重新排隊。',
  'lobby.queueCancelled': '這張排隊票券已經取消了。',
  // ★ C02 §4.2：被指名挑戰的人要看得見挑戰，否則 matchmaker 等不到第二張票。
  'lobby.challenges': '收到的挑戰',
  'lobby.challenges.hint': '指名對決要**雙方互相指名**才會配對成功，所以按下「接受」等於替你排一張反向的票。',
  'lobby.challenges.accept': '接受',
  'lobby.challenges.variant': '玩法',
  'lobby.resume': '你有一場進行中的對局',
  'lobby.resumeAction': '回到對局',
  // ★★ C02 §5 v1.7 ＋ §6.4：`rematch_requested` 走個人頻道，而個人頻道**在大廳也訂著**，
  //   所以剛打完就回大廳的人一樣看得到「有人在等你」。
  //   沒有這一段的話，他一離開結束畫面就再也沒有任何管道知道這件事
  //   （契約要求的「訂閱即回放」在這裡才有意義：重新整理過也還看得到）。
  'lobby.rematchInvite': '{name} 想跟你再戰一場',
  'lobby.rematchInvite.expiresIn': '這個邀請還有 {seconds} 秒。',
  'lobby.rematchInvite.hint': '這個邀請過一段時間會自己失效。',
  'lobby.rematchInvite.action': '回到那一場',
  // ★ C03 §5.1：這一句由 build 的持久性模式決定，理由見本檔頂端的 `guestNoticeCopy`。
  //   **不要**把它改回寫死的字串 —— 那會讓「文案」與「行為」重新變成兩件可以各自跑掉的事。
  'lobby.guestNotice': guestNoticeCopy['zh-TW'][config.guestPersistence],

  'invite.create': '產生邀請碼',
  'invite.code': '邀請碼',
  'invite.link': '分享連結',
  'invite.copy': '複製',
  'invite.copied': '已複製',
  // ★ C02 §4：`join_url` 在後端沒設定大廳 base URL 時是 `null`（**不編造**）。
  //   那條路徑上要顯示邀請碼讓人手動輸入，而不是把 "null" 貼給朋友。
  'invite.linkUnavailable': '這台伺服器沒有設定分享連結，請把上面的邀請碼直接給朋友輸入。',
  'invite.waiting': '等待對手用這個連結加入…',
  'invite.join': '用邀請碼加入',
  'invite.joinPlaceholder': '輸入 6 碼邀請碼',
  'invite.joining': '加入中…',
  // ★ **受邀人**那一側（JoinPage 的落地頁）：他點了一個已經沒用的連結。
  'invite.expired': '這個邀請碼已失效',
  // ★★ **邀請人**那一側（2026-08-19 修復）—— 修復前一句都沒有。
  //   後端 `abandon_no_show()` / 管理員作廢會把 `match_voided` 送到
  //   `match:<invite.match_id>`，而邀請人正訂著那條頻道 —— **位元有送到他的連線上**，
  //   只是這一頁對它零分支：桌子死了，畫面上的邀請碼還亮著，
  //   朋友點下去拿到 404，而邀請人毫不知情。
  //   這一句要同時說完三件事：發生了什麼、不是你的錯、以及**下一步按哪裡**。
  'invite.tableGone':
    '你剛才那個邀請碼已經失效了 —— 沒有人在時限內加入，伺服器把那張桌子收掉了。想再約一次的話，按下面的「產生邀請碼」拿一組新的。',

  'match.round': '第 {n} 回合',
  'match.roundOf': '第 {n} / {total} 回合',
  'match.score': '比分',
  'match.you': '你',
  'match.opponent': '對手',
  'match.chooseNow': '出手！',
  'match.timeLeft': '剩餘 {seconds} 秒',
  'match.deadlinePassed': '時間到，等待伺服器裁定…',
  'match.timeoutPolicy': '逾時不算你輸：伺服器會依照這個玩法的設定替你出手。',
  'match.waitingOpponent': '已送出，等待對手出手',
  'match.waitingOpponent.hint': '這個畫面**刻意**不顯示對手的任何動靜 —— 包含他有沒有出手。',
  'match.yourPick': '你出了',
  'match.opponentPick': '對手出了',
  'match.result.win': '你贏了這一回合',
  'match.result.lose': '這一回合你輸了',
  'match.result.draw': '平手，重來',
  'match.waitingStart': '等待對手入座…',
  'match.ended': '對局結束',
  'match.won': '你贏了！',
  'match.lost': '你輸了',
  'match.drawn': '和局',
  // ★ C02 §6.1：讀不到勝者欄位時**不得**退化成「和局」。
  //   「我讀不到」和「真的平手」對玩家的意義完全不同 ——
  //   把前者顯示成後者，就是在沒有根據的情況下告訴他「你們打平了」。
  'match.resultUnknown': '結果未知',
  'match.resultUnknown.hint':
    '這個版本的前端讀不懂伺服器送來的結束訊息，所以不敢替你判定輸贏。比分與對戰紀錄仍然正確；請重新整理，若還是這樣請回報。',
  // ★ `abandoned`（雙方沒在時限內連上）**不是**「結果未知」。
  //   後端的 `hydrateOutcome()` 只在 `completed` 時填結果，於是 abandoned 落到 unknown、
  //   標題變成「結果未知」—— 那句話的意思是「我讀不懂伺服器」，會讓玩家去回報一個不存在的 bug。
  //   實際發生的事情很單純，而且要說得出來：**沒有人來**。
  'match.abandoned': '對手沒有出現',
  'match.abandoned.hint': '這場對局沒有開始，所以沒有結果 —— 不算你輸，也不會進排行榜。回大廳再找一場吧。',
  'match.voided': '這場對局已被管理員作廢',
  // ★ 管理員在對局中發出的公告（需求 1.2.3）。這是**標籤**，公告內文由管理員自己打，
  //   不進字典 —— 翻譯不了，也不該翻譯。
  'match.announcement': '管理員公告',
  'match.history': '完整對戰紀錄',
  'match.history.round': '回合',
  'match.history.you': '你',
  'match.history.opponent': '對手',
  'match.history.result': '結果',
  'match.rematch': '再來一場',
  'match.rematch.waiting': '已送出再戰請求，等待對手同意',
  // ★ R-5：「按鈕還給你」與「邀請過期了」是**兩件事**，中間隔著一整個 TTL。
  //   伺服器每收到一次輪詢就把意願續期一次，所以邀請真正的死亡時刻是
  //   「最後一次請求 + 2 分鐘」，不是「按下 + 2 分鐘」。
  //   這一句是**尾窗**用的：邀請還活著，只是我們不再主動續期了。
  'match.rematch.stillOpen':
    '對手還沒回應。你的再戰邀請在伺服器上仍然有效 —— 他這段時間一按下，你就會被自動帶進新對局。不想等就直接回大廳。',
  // ★ 這一句是**真的過期之後**才說的：伺服器已經忘記這份邀請，
  //   對手現在才按也不會把你拉進任何對局。
  //   舊版寫「只保留 2 分鐘」是錯的（那是 TTL，不是從按下算起的壽命），已拿掉那個數字。
  'match.rematch.noResponse': '對手一直沒有回應，這次的再戰邀請已經在伺服器上過期了 —— 想再約一次就再按一下。',
  // ★★ C02 §5 v1.7：**被等的那一方**。修復前這幾句一句都不存在 ——
  //   因為協定裡沒有 `rematch_requested`，前端也就沒有任何東西可以說。
  //   使用者兩視窗實測回報的現象逐字是「沒按過再戰的人那一頁完全靜止」。
  'match.rematch.invited': '{name} 想再玩一局',
  // 事件帶了 `expires_at` 才有秒數可講。倒數是從**伺服器給的絕對時刻**減出來的，
  // 不是從收到事件那一刻起算 —— 前端沒有能力從時長還原期限（C02 §6.2 的裁決）。
  'match.rematch.invited.expiresIn': '按下「接受，再來一場」就直接開始。這個邀請還有 {seconds} 秒。',
  // 沒有 `expires_at` 的退化路徑：邀請照樣顯示，但**不編一個秒數出來**。
  'match.rematch.invited.hint': '按下「接受，再來一場」就直接開始。這個邀請過一段時間會自己失效。',
  'match.rematch.accept': '接受，再來一場',
  // ★ A 那一側的撤回（C02 §5 v1.7 的 DELETE）。
  'match.rematch.cancel': '取消再戰',
  // ⚠️ 撤回**失敗**與「請求沒送出」是兩件相反的事：這一句要說得出「邀請可能還在」，
  //   否則玩家會以為自己已經安全了 —— 而他可能正要被拉進一場他不知道的對局。
  'match.rematch.cancelFailed':
    '取消沒有成功，你的再戰邀請可能還在伺服器上 —— 對手這段時間按下的話，你仍然會被帶進新對局。再按一次取消，或留在這一頁等它自己過期。',
  'match.rematch.unavailable': '這場對局沒有正常結束，不能在這裡再戰。回大廳開新的一場吧。',
  'match.rematch.disabled': '這個玩法不開放再戰。',
  'match.rematch.failed': '再戰請求沒有送出去，請再試一次。',
  'match.backToLobby': '回大廳',
  'match.forfeit': '認輸',
  'match.forfeitConfirm': '確定要認輸嗎？這一場會直接判你輸。',
  'match.replay': '看回放',

  'action.rock': '石頭',
  'action.paper': '布',
  'action.scissors': '剪刀',

  'conn.reconnecting': '連線中斷，重新連線中…（第 {attempt} 次）',
  'conn.reconnecting.safe': '別緊張 —— 斷線不算棄權。只有出手逾時才會由伺服器代打。',
  'conn.offline': '目前離線',
  'conn.restored': '連線已恢復',

  'fair.title': '可驗證公平',
  'fair.commit': '本局種子承諾 (server_seed_commit)',
  'fair.commit.hint': '對局開始前就公布了。伺服器沒辦法看到你出什麼之後再改。',
  'fair.seed': '種子原文 (server_seed)',
  'fair.seed.pending': '對局結束後才會公布',
  'fair.transcriptHash': '對戰紀錄雜湊 (transcript_hash)',
  'fair.verify': '在我的瀏覽器裡驗證',
  'fair.verifying': '計算中…',
  'fair.ok': '驗證通過',
  'fair.mismatch': '驗證失敗',
  'fair.missing': '資料不足，無法驗證',
  // ★ C01 §4.2.1 規則 3：遇到不認得的編碼要說「無法驗證」，不能說「驗證失敗」。
  //   兩者對使用者的意義完全不同 —— 前者是「我沒辦法檢查」，後者是「我檢查了，伺服器有問題」。
  //   把它們混為一談，等於在沒有證據的情況下指控伺服器作弊。
  'fair.unsupported': '無法驗證：後端使用了本前端不認得的編碼版本',
  'fair.computed': '我算出來的',
  'fair.expected': '伺服器宣稱的',
  'fair.preimage': '雜湊的原始字串',
  'fair.explain.title': '這在證明什麼？',
  'fair.explain.body':
    '對局一開始，伺服器就公布了 SHA256(server_seed)。結束後才公布 server_seed 原文。你在自己的瀏覽器重算一次，就能確認伺服器沒有中途換掉種子。',
  'fair.explain.limit':
    '它不能證明的事：伺服器仍可能在收到雙方動作後才決定要怎麼用這個種子。要完全排除，需要玩家端也做 commit-reveal（規劃在 v2）。',

  'replay.title': '對局回放',
  'replay.play': '播放',
  'replay.pause': '暫停',
  'replay.step': '下一步',
  'replay.restart': '從頭',
  'replay.loading': '載入回放中…',
  // ★★ C02 §5 規則 2：中途作廢／棄局的對局**也有回放**（200 + `incomplete: true`），
  //   只是只到最後一個已結算的回合。
  //   修復前這條路整個掉進「找不到這場對局」—— 那句話對玩家是一句**斷言**，
  //   而且是錯的：他剛剛才在那場對局裡出過手。
  //   「沒有打完」與「不存在」必須是兩句不同的話。
  'replay.incomplete': '這場對局沒有打完',
  'replay.incomplete.hint':
    '它在中途被作廢或棄局了，所以下面只有已經結算完的回合，沒有最終勝負。在對局中從未揭曉的那一手，不會出現在回放裡 —— 中途作廢不是公開它的理由。',
  // ★ C02 §5 規則 3 逐字寫著「**不是 404**」：對局存在，只是還不能揭露 server_seed。
  'replay.notEnded': '這場對局還沒結束，回放要等它結束之後才會公開',

  'error.generic': '出了點問題',
  'error.notFound': '找不到這場對局',
  'error.rateLimited': '太快了，稍等一下再試',
  'error.network': '連不上伺服器',
  'error.roundMismatch': '回合對不上，正在重新同步',
  // ★ R-6：以下四句取代「把 state.error.message 直接畫成紅色橫幅」。
  //   那個 message 是寫給開發者的英文技術字串，例如 C02 §6.2 的期限退化訊息
  //   `round_started: deadline_at absent — derived from …`。
  //   它現在只進 console（useMatch），畫面只出現這裡的句子。
  'error.protocol': '這個版本的前端跟伺服器對不上，畫面上的部分資訊可能不完整。你的對局本身沒事，重新整理通常就好了。',
  'error.matchNotPlayable': '這場對局現在不能出手（可能還沒開始，或已經結束了）。畫面正在重新同步。',
  'error.alreadySubmitted': '你這一回合已經出手了，正在等對手。',
  'error.illegalAction': '這一手在目前的規則下不能出。',
  'error.retrying': '剛才那個動作還在處理中，稍等一下再試。',
  'error.dismiss': '知道了',
  'common.loading': '載入中…',
  'common.back': '返回',
  'common.close': '關閉',
} as const;

export type StringKey = keyof typeof zhTW;
export type Dict = Record<StringKey, string>;

export const en: Dict = {
  'app.title': 'Rock Paper Scissors',
  'app.subtitle': 'eLab Gaming Platform',
  'app.tagline': 'No signup. Provably fair. Head-to-head.',

  'nav.lobby': 'Lobby',
  'nav.howto': 'How to play',
  'nav.fairness': 'Fairness',
  'lang.switch': '繁體中文',

  'guest.creating': 'Creating an anonymous identity…',
  'guest.you': 'You',
  'guest.failed': 'Could not create an anonymous identity',
  'guest.retry': 'Retry',

  'lobby.title': 'Pick a format',
  'lobby.online': 'Online',
  'lobby.queued': 'In queue',
  'lobby.openTables': 'Open tables',
  'lobby.variant.rps-bo1': 'Single round',
  'lobby.variant.rps-bo3': 'Best of 3',
  'lobby.variant.rps-bo5': 'Best of 5',
  'lobby.variant.rps-first-to-5': 'First to 5',
  'lobby.mode.title': 'Matchmaking',
  'lobby.mode.random': 'Random match',
  'lobby.mode.random.hint': 'Join the queue, take whoever comes',
  'lobby.mode.direct': 'Challenge a player',
  'lobby.mode.direct.hint': 'Name an opponent by subject id (signed-in players only)',
  'lobby.mode.invite': 'Invite code',
  'lobby.mode.invite.hint': 'Create a link and send it to a friend',
  'lobby.mode.vs_ai': 'Play the AI',
  'lobby.mode.vs_ai.hint': 'Face a strategy trained on this platform',
  'lobby.aiProfile': 'AI opponent',
  'lobby.aiProfile.unavailable': 'This server is not offering any AI opponents right now.',
  'lobby.opponentId': 'Opponent subject id',
  'lobby.start': 'Find a match',
  'lobby.queueing': 'Matching…',
  'lobby.cancel': 'Cancel',
  'lobby.queuePosition': 'Waiting {seconds}s',
  'lobby.queueExpired':
    'Matchmaking timed out — your queue ticket expired without finding an opponent. Press again to requeue.',
  'lobby.queueCancelled': 'That queue ticket has been cancelled.',
  'lobby.challenges': 'Challenges you have received',
  'lobby.challenges.hint':
    'A named challenge only matches when **both** sides name each other, so accepting queues a ticket back at them.',
  'lobby.challenges.accept': 'Accept',
  'lobby.challenges.variant': 'Format',
  'lobby.resume': 'You have a match in progress',
  'lobby.resumeAction': 'Return to match',
  'lobby.rematchInvite': '{name} wants a rematch',
  'lobby.rematchInvite.expiresIn': 'This invite has {seconds}s left.',
  'lobby.rematchInvite.hint': 'This invite expires after a while.',
  'lobby.rematchInvite.action': 'Go back to that match',
  'lobby.guestNotice': guestNoticeCopy.en[config.guestPersistence],

  'invite.create': 'Create invite code',
  'invite.code': 'Invite code',
  'invite.link': 'Share link',
  'invite.copy': 'Copy',
  'invite.copied': 'Copied',
  'invite.linkUnavailable':
    'This server has no share link configured — give your friend the invite code above to type in by hand.',
  'invite.waiting': 'Waiting for someone to join with this link…',
  'invite.join': 'Join with a code',
  'invite.joinPlaceholder': 'Enter the 6-character code',
  'invite.joining': 'Joining…',
  'invite.expired': 'That invite code is no longer valid',
  'invite.tableGone':
    'Your invite code has expired — nobody joined in time, so the server cleared that table. Press “Create invite code” below to get a fresh one.',

  'match.round': 'Round {n}',
  'match.roundOf': 'Round {n} of {total}',
  'match.score': 'Score',
  'match.you': 'You',
  'match.opponent': 'Opponent',
  'match.chooseNow': 'Throw!',
  'match.timeLeft': '{seconds}s left',
  'match.deadlinePassed': 'Time is up — waiting for the server to rule…',
  'match.timeoutPolicy': 'Running out of time is not a loss: the server plays for you per this format.',
  'match.waitingOpponent': 'Locked in. Waiting for your opponent.',
  'match.waitingOpponent.hint': 'This screen deliberately shows nothing about your opponent — not even whether they moved.',
  'match.yourPick': 'You threw',
  'match.opponentPick': 'They threw',
  'match.result.win': 'You win the round',
  'match.result.lose': 'You lose the round',
  'match.result.draw': 'Draw — replay',
  'match.waitingStart': 'Waiting for an opponent to sit down…',
  'match.ended': 'Match over',
  'match.won': 'You won!',
  'match.lost': 'You lost',
  'match.drawn': 'Drawn',
  'match.resultUnknown': 'Result unknown',
  'match.resultUnknown.hint':
    'This client could not read the server’s end-of-match message, so it will not guess who won. Scores and the round history above are still correct. Try reloading; if it persists, please report it.',
  'match.abandoned': 'Your opponent never showed up',
  'match.abandoned.hint':
    'The match never started, so there is no result — it is not a loss for you and it does not count towards the leaderboard. Head back to the lobby and find another game.',
  'match.voided': 'An operator voided this match',
  'match.announcement': 'Message from an operator',
  'match.history': 'Full match history',
  'match.history.round': 'Round',
  'match.history.you': 'You',
  'match.history.opponent': 'Opponent',
  'match.history.result': 'Result',
  'match.rematch': 'Rematch',
  'match.rematch.waiting': 'Rematch requested — waiting for your opponent',
  'match.rematch.stillOpen':
    'No answer yet. Your rematch invite is still live on the server — if they press now you will be taken straight into the new match. Head back to the lobby if you would rather not wait.',
  'match.rematch.noResponse':
    'Your opponent never answered and this rematch invite has now expired on the server — press again to invite them once more.',
  'match.rematch.invited': '{name} wants to play again',
  'match.rematch.invited.expiresIn':
    'Press “Accept — play again” and you start straight away. This invite has {seconds}s left.',
  'match.rematch.invited.hint':
    'Press “Accept — play again” and you start straight away. This invite expires after a while.',
  'match.rematch.accept': 'Accept — play again',
  'match.rematch.cancel': 'Cancel rematch',
  'match.rematch.cancelFailed':
    'The cancellation did not go through, so your rematch invite may still be live on the server — if your opponent presses now you will still be pulled into the new match. Press cancel again, or stay on this page until it expires.',
  'match.rematch.unavailable': 'This match did not finish normally, so there is nothing to rematch. Start a fresh one from the lobby.',
  'match.rematch.disabled': 'This format does not allow rematches.',
  'match.rematch.failed': 'The rematch request did not get through. Please try again.',
  'match.backToLobby': 'Back to lobby',
  'match.forfeit': 'Forfeit',
  'match.forfeitConfirm': 'Forfeit this match? It counts as a loss.',
  'match.replay': 'Watch replay',

  'action.rock': 'Rock',
  'action.paper': 'Paper',
  'action.scissors': 'Scissors',

  'conn.reconnecting': 'Connection lost — reconnecting… (attempt {attempt})',
  'conn.reconnecting.safe': 'Relax — a dropped connection is not a forfeit. Only a move timeout is.',
  'conn.offline': 'Offline',
  'conn.restored': 'Connection restored',

  'fair.title': 'Provably fair',
  'fair.commit': 'Seed commitment (server_seed_commit)',
  'fair.commit.hint': 'Published before the match started. The server cannot swap the seed after seeing your move.',
  'fair.seed': 'Seed (server_seed)',
  'fair.seed.pending': 'Published when the match ends',
  'fair.transcriptHash': 'Transcript hash (transcript_hash)',
  'fair.verify': 'Verify in my browser',
  'fair.verifying': 'Hashing…',
  'fair.ok': 'Verified',
  'fair.mismatch': 'Verification failed',
  'fair.missing': 'Not enough data to verify',
  'fair.unsupported': 'The backend used an encoding version this client does not implement',
  'fair.computed': 'Computed here',
  'fair.expected': 'Claimed by server',
  'fair.preimage': 'Hash preimage',
  'fair.explain.title': 'What does this prove?',
  'fair.explain.body':
    'At match start the server published SHA256(server_seed). Only at the end did it publish the seed itself. Recomputing it here proves the seed was not swapped mid-match.',
  'fair.explain.limit':
    'What it does not prove: the server could still decide how to use that seed after seeing both moves. Closing that needs client-side commit-reveal (planned for v2).',

  'replay.title': 'Replay',
  'replay.play': 'Play',
  'replay.pause': 'Pause',
  'replay.step': 'Step',
  'replay.restart': 'Restart',
  'replay.loading': 'Loading replay…',
  'replay.incomplete': 'This match was never finished',
  'replay.incomplete.hint':
    'It was voided or abandoned partway through, so only the rounds that were already settled appear below and there is no final result. Any hand that was never revealed during play stays hidden — being voided is not a reason to expose it.',
  'replay.notEnded': 'This match is still in progress — the replay is published once it ends',

  'error.generic': 'Something went wrong',
  'error.notFound': 'Match not found',
  'error.rateLimited': 'Too fast — try again in a moment',
  'error.network': 'Cannot reach the server',
  'error.roundMismatch': 'Round mismatch — resyncing',
  'error.protocol':
    'This client and the server do not quite agree on the protocol, so some details on screen may be incomplete. Your match itself is fine — reloading usually clears it.',
  'error.matchNotPlayable': 'This match cannot take moves right now (it has not started, or it is already over). Resyncing.',
  'error.alreadySubmitted': 'You have already thrown this round — waiting for your opponent.',
  'error.illegalAction': 'That move is not legal under the current rules.',
  'error.retrying': 'That last action is still being processed. Give it a moment and try again.',
  'error.dismiss': 'Got it',
  'common.loading': 'Loading…',
  'common.back': 'Back',
  'common.close': 'Close',
};

export const dictionaries: Record<'zh-TW' | 'en', Dict> = {
  'zh-TW': zhTW as unknown as Dict,
  en,
};
